import { useEffect, useRef, useState } from "react";
import "./App.css";
import "./Voice.css";
import { Button, Drawer, Flex, message, Skeleton, Slider, Tooltip } from "antd";
import { Typography } from "antd";
import { useNavigate, useParams } from "react-router-dom";

import {
  AppstoreOutlined,
  CaretRightOutlined,
  MutedOutlined,
  OrderedListOutlined,
  PauseOutlined,
  PlayCircleOutlined,
  SoundOutlined,
  StepBackwardOutlined,
  StepForwardOutlined,
} from "@ant-design/icons";
import COS from "cos-js-sdk-v5";
import useSWR from "swr";
import { isMobile } from "react-device-detect";
import { Lrc, LrcLine, useRecoverAutoScrollImmediately } from "react-lrc";
import Loading from "./Loading";
import { motion, AnimatePresence } from "framer-motion";

const { Title, Text } = Typography;

const fetcher = async (url: string) => {
  const res = await fetch(url, {
    headers: new Headers({
      "Access-Control-Allow-Origin": "*",
    }),
  });
  const json = await res.json();
  if (!json.success) {
    const error = new Error((await json).message);
    throw error;
  }
  return json;
};

const timeFormat = (time: number) => {
  const seconds = Math.floor(time % 60);
  const minutes = Math.floor(time / 60);
  return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
};

const Voice = () => {
  const { url } = useParams();
  const [playList, setPlayList] = useState<any>([]);
  const [nowPlayingIndex, setNowPlayingIndex] = useState(0);
  const [nowPlayingVoice, setNowPlayingVoice] = useState<any>({});
  const [loaded, setLoaded] = useState(false);
  const [voiceUrl, setVoiceUrl] = useState("");
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(100);
  const [playListOpen, setPlayListOpen] = useState(false);
  const n = useNavigate();

  const handlePause = () => {
    if (audioRef.current) {
      const audioElement = audioRef.current as HTMLAudioElement;
      if (isPlaying) {
        audioElement.pause();
      } else {
        audioElement.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSeek = () => {
    if (audioRef.current) {
      const audioElement = audioRef.current as HTMLAudioElement;
      setCurrentTime(audioElement.currentTime);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const audioElement = audioRef.current as HTMLAudioElement;
      setCurrentTime(audioElement.currentTime);
    }
  };

  const onAudioEnded = () => {
    if (nowPlayingIndex === playList.length - 1) {
      changePlaying(0);
    } else {
      changePlaying(nowPlayingIndex + 1);
    }
  };

  const onPlayingLast = () => {
    if (nowPlayingIndex === 0) {
      changePlaying(playList.length - 1);
    } else {
      changePlaying(nowPlayingIndex - 1);
    }
  };

  const handleTimeChange = (value: number) => {
    if (audioRef.current) {
      const audioElement = audioRef.current as HTMLAudioElement;
      audioElement.currentTime = value;
      audioElement.play();
      setIsPlaying(true);
      setCurrentTime(value);
    }
  };

  const handleVolumeChange = (value: number) => {
    if (audioRef.current) {
      const audioElement = audioRef.current as HTMLAudioElement;
      setVolume(value);
      audioElement.volume = value / 100;
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      const audioElement = audioRef.current as HTMLAudioElement;
      setDuration(audioElement.duration);
      try {
        audioElement.play();
        setIsPlaying(true);
      } catch (e) {
        console.log(e);
      }
    }
  };

  const lineRenderer = ({
    active,
    line,
  }: {
    active: boolean;
    line: LrcLine;
  }) => {
    return (
      <div className={`line ${active ? "active" : ""}`}>{line.content}</div>
    );
  };

  const changePlaying = (index: number) => {
    if (index === nowPlayingIndex) {
      return;
    }
    setNowPlayingVoice(playList[index]);
    document.title = `${playList[index].title} - ${voiceData.voice.title}`;
    if ("mediaSession" in navigator) {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: playList[index].title,
          artist: "星海玲奈",
          album: voiceData.voice.title,
          artwork: [
            {
              src: playList[index].image
                ? playList[index].image
                : voiceData?.voice.image || "/logo1024.png",
            },
          ],
        });
      }
    setNowPlayingIndex(index);
    if (audioRef.current) {
      const audioElement = audioRef.current as HTMLAudioElement;
      audioElement.play();
      audioElement.currentTime = 0;
      setCurrentTime(0);
      setIsPlaying(true);
    }
  };

  const handleOpenApp = () => {
    const scheme = `ddlynne://voice/${url}`;
    
    // 尝试跳转
    window.location.href = scheme;

    // 设置超时检测
    // 原理：如果成功唤起 APP，页面进入后台，定时器会暂停或延迟执行。
    // 如果 2500ms 后定时器准时执行且页面依然可见，说明唤起大概率失败了。
    const startTime = Date.now();
    const delay = 2500;

    setTimeout(() => {
        const endTime = Date.now();
        // 增加 200ms 的容错缓冲
        if (!document.hidden && (endTime - startTime < delay + 200)) {
            message.error("唤起失败，请检查是否安装最新版 APP 喵~");
        }
    }, delay);
  };

  const { signal } = useRecoverAutoScrollImmediately();

  const {
    data: voiceData,
    error: voiceError,
    isLoading: voiceIsLoading,
  } = useSWR(`https://api.lynne.com.cn/voicePublic/info?url=${url}`, fetcher);

  useEffect(() => {
    if (!url) {
      n("/");
    }
  }, []);

  useEffect(() => {
    if (nowPlayingVoice.file) {
      setLoaded(true);
      const cos = new COS({
        // getAuthorization 必选参数
        getAuthorization: function (options, callback) {
          // 初始化时不会调用，只有调用 cos 方法（例如 cos.putObject）时才会进入
          // 异步获取临时密钥
          // 服务端 JS 和 PHP 例子：https://github.com/tencentyun/cos-js-sdk-v5/blob/master/server/
          // 服务端其他语言参考 COS STS SDK ：https://github.com/tencentyun/qcloud-cos-sts-sdk
          // STS 详细文档指引看：https://cloud.tencent.com/document/product/436/14048
          const url = "https://api.lynne.com.cn/wiki/uploadFile"; // url 替换成您自己的后端服务
          const xhr = new XMLHttpRequest();
          xhr.open("GET", url, true);
          xhr.onload = function (e) {
            let data = null;
            let credentials = null;
            try {
              data = JSON.parse((e.target as XMLHttpRequest).responseText);
              credentials = data.credentials;
            } catch (e) {}
            if (!data || !credentials) {
              return console.error(
                "credentials invalid:\n" + JSON.stringify(data, null, 2)
              );
            }
            callback({
              TmpSecretId: credentials.tmpSecretId,
              TmpSecretKey: credentials.tmpSecretKey,
              SecurityToken: credentials.sessionToken,
              // 建议返回服务器时间作为签名的开始时间，避免用户浏览器本地时间偏差过大导致签名错误
              StartTime: data.startTime, // 时间戳，单位秒，如：1580000000
              ExpiredTime: data.expiredTime, // 时间戳，单位秒，如：1580000000
            });
          };
          xhr.send();
        },
      });

      cos.getObjectUrl(
        {
          Bucket: "lynne-1252518086", // 必须
          Region: "ap-shanghai", // 存储桶所在地域，必须字段
          Key: decodeURI(nowPlayingVoice.file) || "", // 必须
        },
        function (err, data) {
          if (err) {
            // 处理请求出错
            message.error("获取文件失败");
            console.log(err);
          } else {
            // 处理请求成功
            setVoiceUrl(data.Url);
          }
        }
      );
    }
  }, [nowPlayingVoice]);

  useEffect(() => {
    if (voiceData) {
      setPlayList(voiceData.data);
      setNowPlayingVoice(voiceData.data[0]);
      document.title = `${voiceData.data[0].title} - ${voiceData.voice.title}`;
      if ("mediaSession" in navigator) {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: voiceData.data[0].title,
          artist: "星海玲奈",
          album: voiceData.voice.title,
          artwork: [
            {
              src: voiceData.data[0].image
                ? voiceData.data[0].image
                : voiceData?.voice.image || "/logo1024.png",
            },
          ],
        });
      }
      setNowPlayingIndex(0);
    }
  }, [voiceData]);
  if (navigator.mediaSession) {
    navigator.mediaSession.setActionHandler('previoustrack', () => {
      onPlayingLast();
    });
    navigator.mediaSession.setActionHandler('nexttrack', () => {
      onAudioEnded();
    });
    navigator.mediaSession.setActionHandler('seekto', (details) => {
    if (details.fastSeek) {
        // Only use fast seek if supported.
        handleTimeChange(details.seekTime || 0);
        return;
      }
      handleTimeChange(details.seekTime || 0);
      // TODO: Update playback state.
    });
  }
  if (voiceError) {
    return voiceError.message;
  }
  if (voiceIsLoading || voiceData === undefined) return <Loading />;
const currentCover = nowPlayingVoice.image || voiceData?.voice.image || "/logo1024.png";

return (
    <div className="voice-page-container">
      {/* 1. 动态模糊背景 */}
      <div 
        className="voice-background-blur" 
        style={{ backgroundImage: `url(${currentCover})` }} 
      />

      {/* 2. 音频核心 */}
      {loaded && (
        <audio
          ref={audioRef}
          src={voiceUrl}
          onSeeked={handleSeek}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={onAudioEnded}
        />
      )}

      {/* 3. 抽屉式播放列表 */}
      <Drawer
        title={<div style={{textAlign:'center'}}>播放列表 ({playList.length})</div>}
        placement="bottom"
        height="60%"
        closable={false} 
        onClose={() => setPlayListOpen(false)}
        open={playListOpen}
        styles={{ 
            content: { borderRadius: '24px 24px 0 0', overflow: 'hidden', background: 'rgba(255,255,255,0.95)' },
            body: { padding: 0 }
        }}
      >
        <div style={{ padding: '10px 0' }}>
            {playList.map((listItem: any, index: number) => (
            <div
                key={listItem.id}
                className={`voice-list-item ${nowPlayingIndex === index ? "playing" : ""}`}
                onClick={() => {
                    changePlaying(index);
                    if (isMobile) setPlayListOpen(false);
                }}
            >
                <div style={{ width: 24, textAlign: 'center' }}>
                    {nowPlayingIndex === index ? <PlayCircleOutlined /> : <span style={{fontSize: 12, color:'#999'}}>{index + 1}</span>}
                </div>
                <div style={{ flex: 1, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    {listItem.title}
                </div>
            </div>
            ))}
        </div>
      </Drawer>

      {/* 4. 界面渲染 */}
      {isMobile ? (
        // === 移动端视图 ===
        <div className="mobile-container">
          {/* 顶部简易标题 */}
          <div style={{ 
              position: 'relative', // 父容器相对定位
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'flex-end', // 内容向右对齐(为了放按钮)
              padding: '15px 15px', 
              flexShrink: 0,
              height: 64 // 固定高度，防止跳动
          }}>
             
             {/* 1. 绝对定位的标题：确保永远居中 */}
             <div style={{
                 position: 'absolute',
                 left: 0,
                 right: 0,
                 top: 0,
                 bottom: 0,
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
                 padding: '0 110px', // 左右设置 padding，防止标题覆盖按钮
                 pointerEvents: 'none', // 让点击事件穿透（如果需要）
             }}>
                <Text strong style={{ fontSize: 16, color: '#333' }} ellipsis>
                    {nowPlayingVoice.title}
                </Text>
             </div>
             
             {/* 2. 右侧按钮：添加文字，调整样式 */}
             <Button 
                type="primary"
                ghost // 使用幽灵按钮或 default，看设计喜好
                shape="round" 
                size="small"
                icon={<AppstoreOutlined />} 
                onClick={handleOpenApp}
                style={{ 
                    zIndex: 1, // 确保在标题上方
                    fontSize: 12, 
                    fontWeight: 500,
                    boxShadow: 'none',
                    background: 'rgba(255,255,255,0.5)', // 半透明背景增加可读性
                    border: '1px solid rgba(0,0,0,0.1)'
                }}
             >
                在APP中打开
             </Button>
          </div>

          {/* 中间弹性区域：歌词 */}
          <div className="mobile-lrc-area">
              {nowPlayingVoice.lrc ? (
                <Lrc
                  lrc={nowPlayingVoice.lrc}
                  currentMillisecond={currentTime * 1000}
                  lineRenderer={lineRenderer}
                  verticalSpace={true}
                  recoverAutoScrollSingal={signal}
                  recoverAutoScrollInterval={5000}
                  style={{ height: "100%", width: "100%", overflowX: 'hidden' }}
                />
              ) : (
                <div className="plain-text">
                    {nowPlayingVoice.text
                      ? nowPlayingVoice.text
                      : "暂时没有文本喔"}
                  </div>
              )}
          </div>

          {/* 底部固定控制栏 */}
          <div className="mobile-footer">
            <Flex gap={12} align="center" style={{ marginBottom: 12 }}>
                 <div style={{ width: 48, height: 48, position: 'relative', flexShrink: 0 }}>
                   <AnimatePresence mode="wait">
                     <motion.img
                        key={currentCover}
                        src={currentCover}
                        initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
                        animate={{ opacity: 1, scale: 1, rotate: 0 }}
                        exit={{ opacity: 0, scale: 0.8, rotate: 10 }}
                        transition={{ duration: 0.3 }}
                        style={{ width: '100%', height: '100%', borderRadius: 8, objectFit: 'cover', border: '1px solid rgba(0,0,0,0.1)', position: 'absolute', top: 0, left: 0 }}
                        alt="cover"
                     />
                   </AnimatePresence>
                 </div>
                 <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 'bold', fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {nowPlayingVoice.title} - {voiceData?.voice.title}
                    </div>
                    <Flex justify="space-between" align="center" style={{marginTop: 4}}>
                         <span style={{ fontSize: 11, color: '#666' }}>
                             {timeFormat(currentTime)} / {timeFormat(duration)}
                         </span>
                    </Flex>
                 </div>
            </Flex>
            
            <Slider
                className="custom-music-slider"
                value={currentTime}
                max={duration}
                onChange={handleTimeChange}
                step={0.1}
                tooltip={{ open: false }}
                style={{ margin: '0 0 16px 0' }}
            />

            <Flex justify="space-between" align="center">
                <div style={{ width: 32 }}></div> {/* 占位保持对齐 */}
                
                <Flex gap={24} align="center">
                    <StepBackwardOutlined style={{ fontSize: 26, color: '#333' }} onClick={onPlayingLast} />
                    <div 
                        onClick={handlePause}
                        style={{
                            width: 56, height: 56, 
                            borderRadius: '50%', background: '#1677ff', 
                            color:'#fff', display:'flex', alignItems:'center', justifyContent:'center',
                            boxShadow: '0 4px 12px rgba(22, 119, 255, 0.4)'
                        }}
                    >
                        {isPlaying ? <PauseOutlined style={{ fontSize: 26 }} /> : <CaretRightOutlined style={{ fontSize: 26, marginLeft: 3 }} />}
                    </div>
                    <StepForwardOutlined style={{ fontSize: 26, color: '#333' }} onClick={onAudioEnded} />
                </Flex>
                <Button type="text" icon={<OrderedListOutlined style={{ fontSize: 20, color:'#555' }} />} onClick={() => setPlayListOpen(true)} />
                
            </Flex>
          </div>
        </div>
      ) : (
        // === PC 端视图 (响应式卡片) ===
        <div className="glass-player-card">
          {/* 左侧面板 */}
          <Flex vertical className="pc-left-panel" style={{ width: '40%', minWidth: 280, paddingRight: 30, borderRight: '1px solid rgba(0,0,0,0.06)' }}>
             <Flex vertical align="center" className="pc-control-group" style={{ width: '100%', marginTop: 'auto', marginBottom: 'auto' }}>
                <div style={{ width: 'clamp(180px, 25vw, 280px)', height: 'clamp(180px, 25vw, 280px)', position: 'relative', marginBottom: 20 }}>
                    <AnimatePresence mode="wait">
                        <motion.img 
                            key={currentCover}
                            src={currentCover} 
                            className="album-cover" 
                            alt="cover"
                            // 动画效果：缩小淡出 -> 放大淡入
                            initial={{ opacity: 0, scale: 0.9, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: -10 }}
                            transition={{ duration: 0.4, ease: "easeOut" }}
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                        />
                    </AnimatePresence>
                </div>
                <Title level={4} style={{ margin: "20px 0 5px 0", textAlign: 'center', width: '100%' }} ellipsis={{tooltip: nowPlayingVoice.title}}>
                    {nowPlayingVoice.title}
                </Title>
                <Text type="secondary" style={{fontSize: 13}}>{voiceData?.voice.title}</Text>
             </Flex>

             {/* 进度与控制 */}
             <div style={{ width: '100%', marginTop: 20 }}>
                 <Flex align="center" gap={8}>
                    <span style={{ fontSize: 12, width: 35, textAlign:'right', color:'#666' }}>{timeFormat(currentTime)}</span>
                    <Slider
                        className="custom-music-slider"
                        value={currentTime}
                        max={duration}
                        onChange={handleTimeChange}
                        step={0.1}
                        style={{ flex: 1 }}
                        tooltip={{ formatter: (value) => {

                      if (value) {

                        return timeFormat(value);

                      } else {

                        return "";

                      }

                    }, }}
                    />
                    <span style={{ fontSize: 12, width: 35, color:'#666' }}>{timeFormat(duration)}</span>
                 </Flex>

                 <Flex justify="center" align="center" gap={25} style={{ margin: '15px 0' }}>
                    <StepBackwardOutlined className="hover-scale" style={{ fontSize: 24, color: '#444', cursor: 'pointer' }} onClick={onPlayingLast} />
                    
                    <Button
                        type="primary"
                        shape="circle"
                        size="large"
                        style={{ width: 56, height: 56, display:'flex', justifyContent:'center', alignItems:'center', boxShadow: '0 4px 12px rgba(22, 119, 255, 0.3)' }}
                        icon={isPlaying ? <PauseOutlined style={{ fontSize: 24 }} /> : <CaretRightOutlined style={{ fontSize: 26, marginLeft: 4 }} />}
                        onClick={handlePause}
                    />

                    <StepForwardOutlined className="hover-scale" style={{ fontSize: 24, color: '#444', cursor: 'pointer' }} onClick={onAudioEnded} />
                 </Flex>

                 <Flex justify="space-between" align="center" style={{ marginTop: 10 }}>
                     <Flex gap={8} align="center" style={{ width: 100 }}>
                         <div onClick={() => setVolume(volume === 0 ? 100 : 0)} style={{ cursor: 'pointer', color:'#666' }}>
                            {volume === 0 ? <MutedOutlined /> : <SoundOutlined />}
                         </div>
                         <Slider value={volume} onChange={handleVolumeChange} style={{ flex: 1 }} />
                     </Flex>
                     <Button type="text" size="small" icon={<OrderedListOutlined />} onClick={() => setPlayListOpen(true)}>
                        列表
                     </Button>
                 </Flex>
             </div>
          </Flex>

          {/* 右侧面板：歌词 */}
          <div className="pc-right-panel" style={{ flex: 1, paddingLeft: 30, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div className="lrc-wrapper">
                {nowPlayingVoice.lrc ? (
                  <Lrc
                    lrc={nowPlayingVoice.lrc}
                    currentMillisecond={currentTime * 1000}
                    lineRenderer={lineRenderer}
                    verticalSpace={true}
                    recoverAutoScrollSingal={signal}
                    recoverAutoScrollInterval={5000}
                    style={{ height: "100%", overflowX: 'hidden' }}
                  />
                ) : (
                  <div className="plain-text">
                    {nowPlayingVoice.text
                      ? nowPlayingVoice.text
                      : "暂时没有文本喔"}
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Voice;
